import React from 'react';
import './AlertSteps.css';

/**
 * You changed what this alert measures. Look at it before you save (PRD-1382).
 *
 * **The gate exists because the failure is invisible.** On create, a bad
 * generation is caught by the author looking at the preview and rejecting it.
 * On edit there is nothing forcing that look: the alert already worked, the
 * screen already showed a sensible number, and a regenerated query can match
 * something quite different while the screen still reads plausibly. The same
 * sentence produces different SQL — measured at accepted once in six runs at
 * identical scope.
 *
 * So a measurement change cannot be saved until it has been seen. Not a warning
 * to read past: the step does not advance while this is unanswered.
 *
 * **It also says what will happen to the arming**, because that is the part an
 * author would otherwise discover from an alert that stopped firing. The server
 * disarms on a measurement change and the re-arm is a deliberate, separately
 * audited act; telling someone that after the fact, via silence, is the worst
 * version of it.
 *
 * `hasPreview` distinguishes "confirm what you can see" from "there is nothing
 * to confirm yet" — offering a confirm button over an empty screen asks the
 * author to attest to something that was never shown, which is the ritual this
 * gate is supposed to replace rather than become.
 */
const AlertMeasurementGate = ({ acknowledged = false, onAcknowledge = null, hasPreview = false }) => {
  if (acknowledged) {
    return (
      <p className="alert-measurement-gate alert-measurement-gate-done" role="status">
        Checked. This alert will be deactivated when you save, so review it and switch it back on
        when you are happy with it.
      </p>
    );
  }

  return (
    <div className="alert-measurement-gate" role="alert">
      <p className="alert-measurement-gate-title">You changed what this alert measures</p>
      {hasPreview ? (
        <>
          <p>
            Check the rows above are what you expect. A rebuilt query can match something different
            from the one you started with, and nothing else on this screen would show that.
          </p>
          <p>
            Saving will also deactivate this alert, so it cannot fire on the new definition before
            anyone has looked at it. Switch it back on when you are happy.
          </p>
          <button
            type="button"
            className="alert-measurement-gate-confirm"
            onClick={onAcknowledge}
          >
            I have checked these rows
          </button>
        </>
      ) : (
        <p>
          Build the alert to see what it now matches. It cannot be saved until you have looked at
          the result.
        </p>
      )}
    </div>
  );
};

export default AlertMeasurementGate;
